/**
 * Admin Operation Costs Page
 * Interface for managing configurable operation costs
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui';
import { adminOperationCostsService, CostConfiguration, ConfigurationAudit, ManufacturerOverride } from '../services/admin-operation-costs.service';
import { ManufacturerDetails } from '../services/admin.service';
import { currencyFormattingService } from '../services/currency-formatting.service';

interface EditingConfig {
  operationType: string;
  baseRate: number;
  baseCoverageMonths?: number;
  monthlySurchargePercent?: number;
  reason: string;
}

interface CreateConfigForm {
  operationType: string;
  baseRate: number;
  baseCoverageMonths?: number;
  monthlySurchargePercent?: number;
  reason: string;
}

interface OverrideForm {
  id?: string;
  manufacturerId: string;
  operationType: string;
  discountType: 'percent' | 'fixed';
  discountValue: number;
  maxUnits?: number | null;
  applicableUnits?: number | null;
  isActive: boolean;
  startsAt?: string;
  endsAt?: string;
}

const OPERATION_TYPE_OPTIONS = [
  { value: 'analytics_report', label: 'Analytics Report' },
  { value: 'qr_integration_access', label: 'QR Integration Access' },
  { value: 'confidential_info_access', label: 'Confidential Info Access' },
  { value: 'batch_creation', label: 'Batch Creation' },
];

const AdminOperationCostsPage: React.FC = () => {
  const navigate = useNavigate();
  const [configurations, setConfigurations] = useState<CostConfiguration[]>([]);
  const [auditHistory, setAuditHistory] = useState<ConfigurationAudit[]>([]);
  const [overrides, setOverrides] = useState<ManufacturerOverride[]>([]);
  const [manufacturers, setManufacturers] = useState<ManufacturerDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingConfig, setEditingConfig] = useState<EditingConfig | null>(null);
  const [createConfig, setCreateConfig] = useState<CreateConfigForm>({
    operationType: '',
    baseRate: 0,
    baseCoverageMonths: 3,
    monthlySurchargePercent: 0,
    reason: '',
  });
  const [overrideForm, setOverrideForm] = useState<OverrideForm>({
    manufacturerId: '',
    operationType: '',
    discountType: 'percent',
    discountValue: 0,
    maxUnits: undefined,
    applicableUnits: undefined,
    isActive: true,
    startsAt: '',
    endsAt: '',
  });
  const [saving, setSaving] = useState(false);
  const [creatingConfig, setCreatingConfig] = useState(false);
  const [savingOverride, setSavingOverride] = useState(false);
  const [activeTab, setActiveTab] = useState<'configurations' | 'history' | 'overrides'>('configurations');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [configsData, historyData, overridesData, manufacturersData] = await Promise.all([
        adminOperationCostsService.getAllConfigurations(),
        adminOperationCostsService.getAuditHistory(),
        adminOperationCostsService.listOverrides(),
        adminOperationCostsService.listManufacturers(),
      ]);

      setConfigurations(configsData);
      setAuditHistory(historyData);
      setOverrides(overridesData);
      setManufacturers(manufacturersData);
    } catch (err: any) {
      console.error('Error loading operation costs data:', err);
      setError(err.response?.data?.error || 'Failed to load operation costs data');
    } finally {
      setLoading(false);
    }
  };

  const handleOverrideSubmit = async () => {
    if (!overrideForm.manufacturerId || !overrideForm.operationType) {
      alert('Manufacturer ID and operation type are required');
      return;
    }
    if (overrideForm.discountValue < 0) {
      alert('Discount value cannot be negative');
      return;
    }

    try {
      setSavingOverride(true);
      if (overrideForm.id) {
        await adminOperationCostsService.updateOverride(overrideForm.id, overrideForm);
      } else {
        await adminOperationCostsService.createOverride(overrideForm);
      }
      setOverrideForm({
        manufacturerId: '',
        operationType: '',
        discountType: 'percent',
        discountValue: 0,
        maxUnits: undefined,
        applicableUnits: undefined,
        isActive: true,
        startsAt: '',
        endsAt: '',
      });
      await loadData();
    } catch (err: any) {
      console.error('Error saving override:', err);
      alert(err.response?.data?.error || 'Failed to save override');
    } finally {
      setSavingOverride(false);
    }
  };

  const handleEditOverride = (ov: ManufacturerOverride) => {
    setActiveTab('overrides');
    setOverrideForm({
      id: ov.id,
      manufacturerId: ov.manufacturerId,
      operationType: ov.operationType,
      discountType: ov.discountType,
      discountValue: ov.discountValue,
      maxUnits: ov.maxUnits ?? undefined,
      applicableUnits: ov.applicableUnits ?? undefined,
      isActive: ov.isActive,
      startsAt: ov.startsAt || '',
      endsAt: ov.endsAt || '',
    });
  };

  const handleEdit = (config: CostConfiguration) => {
    setEditingConfig({
      operationType: config.operationType,
      baseRate: config.baseRate,
      baseCoverageMonths: config.baseCoverageMonths,
      monthlySurchargePercent: config.monthlySurchargePercent,
      reason: '',
    });
  };

  const handleCancelEdit = () => {
    setEditingConfig(null);
  };

  const handleSave = async () => {
    if (!editingConfig) return;

    // Validation
    if (editingConfig.baseRate < 0) {
      alert('Cost cannot be negative');
      return;
    }

    if (!editingConfig.reason.trim()) {
      alert('Please provide a reason for this change');
      return;
    }

    if (editingConfig.monthlySurchargePercent !== undefined && editingConfig.monthlySurchargePercent < 0) {
      alert('Monthly surcharge percent cannot be negative');
      return;
    }

    try {
      setSaving(true);
      await adminOperationCostsService.updateConfiguration(
        editingConfig.operationType,
        editingConfig.baseRate,
        editingConfig.reason,
        editingConfig.baseCoverageMonths,
        editingConfig.monthlySurchargePercent
      );

      // Reload data to reflect changes
      await loadData();
      setEditingConfig(null);
    } catch (err: any) {
      console.error('Error updating configuration:', err);
      alert(err.response?.data?.error || 'Failed to update configuration');
    } finally {
      setSaving(false);
    }
  };

  const resetCreateConfig = () => {
    setCreateConfig({
      operationType: '',
      baseRate: 0,
      baseCoverageMonths: 3,
      monthlySurchargePercent: 0,
      reason: '',
    });
  };

  const handleCreateConfig = async () => {
    const operationType = createConfig.operationType.trim().toLowerCase();
    if (!operationType) {
      alert('Operation type is required');
      return;
    }

    if (createConfig.baseRate < 0) {
      alert('Cost cannot be negative');
      return;
    }

    if (!createConfig.reason.trim()) {
      alert('Please provide a reason for this configuration');
      return;
    }

    if (createConfig.monthlySurchargePercent !== undefined && createConfig.monthlySurchargePercent < 0) {
      alert('Monthly surcharge percent cannot be negative');
      return;
    }

    try {
      setCreatingConfig(true);
      await adminOperationCostsService.updateConfiguration(
        operationType,
        createConfig.baseRate,
        createConfig.reason,
        createConfig.baseCoverageMonths,
        createConfig.monthlySurchargePercent
      );
      resetCreateConfig();
      await loadData();
    } catch (err: any) {
      console.error('Error creating configuration:', err);
      alert(err.response?.data?.error || 'Failed to create configuration');
    } finally {
      setCreatingConfig(false);
    }
  };

  const formatCurrency = (amount: number, currency: string = 'INR'): string => {
    return currencyFormattingService.formatCurrency(amount, currency);
  };

  const formatOperationType = (type: string): string => {
    return type
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading operation costs...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <Button onClick={loadData}>Retry</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <button
                onClick={() => navigate('/admin')}
                className="text-gray-500 hover:text-gray-700"
                aria-label="Back to admin dashboard"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <span className="text-sm text-gray-500">Admin Dashboard</span>
              <span className="text-sm text-gray-500">/</span>
              <span className="text-sm text-gray-700">Operation Costs</span>
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Operation Cost Configuration</h1>
            <p className="text-sm text-gray-600 mt-1">Manage pricing for system operations</p>
          </div>
          <Button variant="outline" onClick={loadData} disabled={loading}>
            <svg className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </Button>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px" role="tablist" aria-label="Operation costs sections">
              <button
                type="button"
                onClick={() => setActiveTab('configurations')}
                role="tab"
                aria-selected={activeTab === 'configurations'}
                className={`px-6 py-3 text-sm font-medium border-b-2 ${
                  activeTab === 'configurations'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Cost Configurations
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('history')}
                role="tab"
                aria-selected={activeTab === 'history'}
                className={`px-6 py-3 text-sm font-medium border-b-2 ${
                  activeTab === 'history'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Audit History ({auditHistory.length})
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('overrides')}
                role="tab"
                aria-selected={activeTab === 'overrides'}
                className={`px-6 py-3 text-sm font-medium border-b-2 ${
                  activeTab === 'overrides'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Manufacturer Overrides
              </button>
            </nav>
          </div>
        </div>

        {/* Configurations Tab */}
        {activeTab === 'configurations' && (
          <div className="space-y-6">
            {/* Info Banner */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex">
                <svg className="w-5 h-5 text-blue-600 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <h3 className="text-sm font-medium text-blue-900">About Operation Costs</h3>
                  <p className="text-sm text-blue-700 mt-1">
                    These costs are applied to various system operations. Changes take effect immediately for new operations.
                    All changes are logged in the audit history for accountability.
                  </p>
                </div>
              </div>
            </div>

            {/* Add Operation Cost */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Add Operation Cost</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Operation Type</label>
                  <select
                    className="mt-1 w-full border rounded px-3 py-2 bg-white text-gray-900"
                    value={createConfig.operationType}
                    onChange={(e) => setCreateConfig({ ...createConfig, operationType: e.target.value })}
                  >
                    <option value="">Select operation type</option>
                    {OPERATION_TYPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Base Rate</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className="mt-1 w-full border rounded px-3 py-2 bg-white text-gray-900"
                    value={createConfig.baseRate}
                    onChange={(e) => setCreateConfig({ ...createConfig, baseRate: parseFloat(e.target.value) || 0 })}
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Coverage Months</label>
                  <input
                    type="number"
                    min="0"
                    className="mt-1 w-full border rounded px-3 py-2 bg-white text-gray-900"
                    value={createConfig.baseCoverageMonths ?? ''}
                    onChange={(e) =>
                      setCreateConfig({
                        ...createConfig,
                        baseCoverageMonths: e.target.value ? parseInt(e.target.value, 10) : undefined
                      })
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Surcharge %/Month</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className="mt-1 w-full border rounded px-3 py-2 bg-white text-gray-900"
                    value={createConfig.monthlySurchargePercent ?? ''}
                    onChange={(e) =>
                      setCreateConfig({
                        ...createConfig,
                        monthlySurchargePercent: e.target.value ? parseFloat(e.target.value) : undefined
                      })
                    }
                  />
                </div>
              </div>
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={createConfig.reason}
                  onChange={(e) => setCreateConfig({ ...createConfig, reason: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 bg-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  placeholder="Explain why this cost is being created..."
                />
              </div>
              <div className="mt-4 flex gap-2">
                <Button onClick={handleCreateConfig} disabled={creatingConfig}>
                  {creatingConfig ? 'Saving...' : 'Add Configuration'}
                </Button>
                <Button variant="outline" onClick={resetCreateConfig} disabled={creatingConfig}>
                  Clear
                </Button>
              </div>
            </div>

            {/* Configurations List */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200" aria-label="Cost configurations">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Operation Type
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Cost Type
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Current Rate
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Last Updated
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {configurations.map((config) => (
                    <tr key={config.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {formatOperationType(config.operationType)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          {config.costType.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {editingConfig?.operationType === config.operationType ? (
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <input
                                type="number"
                                step="0.0001"
                                min="0"
                                value={editingConfig.baseRate}
                                onChange={(e) => {
                                  if (editingConfig) {
                                    setEditingConfig({
                                      ...editingConfig,
                                      baseRate: parseFloat(e.target.value) || 0,
                                    });
                                  }
                                }}
                                className="w-32 px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-900 bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="0.00"
                              />
                              <span className="text-sm text-gray-600 font-medium">{config.currency}</span>
                            </div>
                            <div className="flex items-center gap-3 text-xs text-gray-600">
                              <div className="flex items-center gap-1">
                                <span>Coverage (months):</span>
                                <input
                                  type="number"
                                  min="0"
                                  className="w-16 px-2 py-1 border rounded text-xs"
                                  value={editingConfig.baseCoverageMonths ?? ''}
                                  onChange={(e) => setEditingConfig(prev => prev ? { ...prev, baseCoverageMonths: e.target.value ? parseInt(e.target.value, 10) : undefined } : null)}
                                />
                              </div>
                              <div className="flex items-center gap-1">
                                <span>Surcharge %/mo:</span>
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  className="w-20 px-2 py-1 border rounded text-xs"
                                  value={editingConfig.monthlySurchargePercent ?? ''}
                                  onChange={(e) => setEditingConfig(prev => prev ? { ...prev, monthlySurchargePercent: e.target.value ? parseFloat(e.target.value) : undefined } : null)}
                                />
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="text-sm text-gray-900 font-medium">
                            {formatCurrency(config.baseRate, config.currency)}
                            {config.costType === 'per_product' && (
                              <span className="text-xs text-gray-500 ml-1">per product</span>
                            )}
                            {(config.baseCoverageMonths !== undefined || config.monthlySurchargePercent !== undefined) && (
                              <div className="text-xs text-gray-500">
                                Coverage: {config.baseCoverageMonths ?? 0} mo · Surcharge: {config.monthlySurchargePercent ?? 0}%
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          config.isActive
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {config.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(config.updatedAt).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        {editingConfig?.operationType === config.operationType ? (
                          <div className="flex justify-end gap-2">
                            <Button
                              size="sm"
                              onClick={handleSave}
                              disabled={saving}
                            >
                              {saving ? 'Saving...' : 'Save'}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={handleCancelEdit}
                              disabled={saving}
                            >
                              Cancel
                            </Button>
                          </div>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEdit(config)}
                          >
                            Edit
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Edit Form (when editing) */}
            {editingConfig && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Editing: {editingConfig ? formatOperationType(editingConfig.operationType) : ''}
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Reason for Change <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={editingConfig.reason}
                      onChange={(e) => setEditingConfig(prev => prev ? {
                        ...prev,
                        reason: e.target.value,
                      } : null)}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 bg-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                      placeholder="Explain why this cost is being changed..."
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      This reason will be recorded in the audit history
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Audit History Tab */}
        {activeTab === 'history' && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            {auditHistory.length === 0 ? (
              <div className="p-8 text-center">
                <p className="text-gray-500">No audit history available</p>
              </div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200" aria-label="Cost configuration audit history">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date & Time
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Operation Type
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Change Type
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Old Value
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      New Value
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Changed By
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Reason
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {auditHistory.map((audit) => (
                    <tr key={audit.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {new Date(audit.changedAt).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatOperationType(audit.operationType)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          audit.changeType === 'create' ? 'bg-green-100 text-green-800' :
                          audit.changeType === 'update' ? 'bg-blue-100 text-blue-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {audit.changeType}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {audit.oldValue !== null ? formatCurrency(audit.oldValue) : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(audit.newValue)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {audit.changedBy}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {audit.reason || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {activeTab === 'overrides' && (
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 text-gray-900">
            <div className="lg:col-span-3 bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold mb-4">Manufacturer Overrides</h2>
              <div className="space-y-4">
                {overrides.length === 0 && <div className="text-gray-500 text-sm">No overrides configured.</div>}
                {overrides.map((ov) => (
                  <div key={ov.id} className="border rounded-lg p-3 hover:border-blue-300">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-semibold text-gray-800">
                          {ov.operationType} · {ov.discountValue}{ov.discountType === 'percent' ? '%' : ' flat'}
                          {ov.maxUnits ? ` (up to ${ov.maxUnits} units)` : ''}
                        </div>
                        <div className="text-sm text-gray-600">
                          Manufacturer: {ov.manufacturerId} · Active: {ov.isActive ? 'Yes' : 'No'}
                        </div>
                        <div className="text-xs text-gray-500">
                          Created {new Date(ov.createdAt).toLocaleString()}
                          {ov.startsAt ? ` · Starts ${new Date(ov.startsAt).toLocaleDateString()}` : ''}
                          {ov.endsAt ? ` · Ends ${new Date(ov.endsAt).toLocaleDateString()}` : ''}
                        </div>
                      </div>
                      <Button variant="outline" onClick={() => handleEditOverride(ov)}>Edit</Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-2 bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold mb-4">{overrideForm.id ? 'Edit Override' : 'Create Override'}</h2>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Manufacturer ID</label>
                  <select
                    className="mt-1 w-full border rounded px-3 py-2 bg-white text-gray-900"
                    value={overrideForm.manufacturerId}
                    onChange={(e) => setOverrideForm({ ...overrideForm, manufacturerId: e.target.value })}
                  >
                    <option value="">Select manufacturer</option>
                    {manufacturers.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name} ({m.id})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Operation Type</label>
                  <select
                    className="mt-1 w-full border rounded px-3 py-2 bg-white text-gray-900"
                    value={overrideForm.operationType}
                    onChange={(e) => setOverrideForm({ ...overrideForm, operationType: e.target.value })}
                  >
                    <option value="">Select operation type</option>
                    {OPERATION_TYPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Discount Type</label>
                    <select
                    className="mt-1 w-full border rounded px-3 py-2 bg-white text-gray-900"
                    value={overrideForm.discountType}
                    onChange={(e) => setOverrideForm({ ...overrideForm, discountType: e.target.value as 'percent' | 'fixed' })}
                  >
                    <option value="percent">Percent</option>
                    <option value="fixed">Fixed</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Discount Value</label>
                    <input
                      type="number"
                    className="mt-1 w-full border rounded px-3 py-2 bg-white text-gray-900"
                    value={overrideForm.discountValue}
                    onChange={(e) => setOverrideForm({ ...overrideForm, discountValue: parseFloat(e.target.value) })}
                  />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Max Units</label>
                    <input
                      type="number"
                    className="mt-1 w-full border rounded px-3 py-2 bg-white text-gray-900"
                    value={overrideForm.maxUnits ?? ''}
                    onChange={(e) => setOverrideForm({ ...overrideForm, maxUnits: e.target.value ? parseInt(e.target.value, 10) : undefined })}
                  />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Applicable Units</label>
                    <input
                      type="number"
                    className="mt-1 w-full border rounded px-3 py-2 bg-white text-gray-900"
                    value={overrideForm.applicableUnits ?? ''}
                    onChange={(e) => setOverrideForm({ ...overrideForm, applicableUnits: e.target.value ? parseInt(e.target.value, 10) : undefined })}
                  />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Starts At</label>
                    <input
                      type="date"
                    className="mt-1 w-full border rounded px-3 py-2 bg-white text-gray-900"
                    value={overrideForm.startsAt}
                    onChange={(e) => setOverrideForm({ ...overrideForm, startsAt: e.target.value })}
                  />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Ends At</label>
                    <input
                      type="date"
                    className="mt-1 w-full border rounded px-3 py-2 bg-white text-gray-900"
                    value={overrideForm.endsAt}
                    onChange={(e) => setOverrideForm({ ...overrideForm, endsAt: e.target.value })}
                  />
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    id="override-active"
                    type="checkbox"
                    checked={overrideForm.isActive}
                    onChange={(e) => setOverrideForm({ ...overrideForm, isActive: e.target.checked })}
                  />
                  <label htmlFor="override-active" className="text-sm text-gray-700">Active</label>
                </div>
                <div className="flex space-x-2">
                  <Button onClick={handleOverrideSubmit} disabled={savingOverride}>
                    {overrideForm.id ? 'Update Override' : 'Create Override'}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setOverrideForm({
                      manufacturerId: '',
                      operationType: '',
                      discountType: 'percent',
                      discountValue: 0,
                      maxUnits: undefined,
                      applicableUnits: undefined,
                      isActive: true,
                      startsAt: '',
                      endsAt: '',
                    })}
                  >
                    Clear
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
    </div>
  );
};

export default AdminOperationCostsPage;

